import { ModalWrapper } from '@/components/atoms/modal-wrapper'
import type { SlotFailureReason } from '@/features/checkIn/identification-slice'
import { css } from '@emotion/react'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

export type RetakeFailureDisplay = {
  heading: string
  tips?: { lead: string; items: string[] }
}

/** 読み取れなかった失敗（判定NGと400）は撮り直しで直るため、書類別の文言を出す。通信の失敗は共通の文言 */
export const retakeDisplayForReason = (reason: SlotFailureReason, retakeDisplay: RetakeFailureDisplay): RetakeFailureDisplay | undefined =>
  reason === 'retakeRequired' || reason === 'unreadable' ? retakeDisplay : undefined

type Props = {
  retake?: RetakeFailureDisplay
  onDismiss: () => void
}

const ErrorDialogAutoCloseMs = 5000

export const IdentityVerifyErrorDialog: React.FC<Props> = ({ retake, onDismiss }) => {
  const { t } = useTranslation()

  useEffect(() => {
    const timer = setTimeout(onDismiss, ErrorDialogAutoCloseMs)
    return () => clearTimeout(timer)
    // onDismiss を依存に入れると呼び出し側の再レンダーごとにタイマーが張り直され、閉じなくなる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const heading = retake?.heading ?? t('Failed to upload. Please try again.')

  return (
    <ModalWrapper isOpen onClose={onDismiss} shouldCloseOnOverlayClick modalStyle={cardContainerStyle} overlayStyle={overlayStyle}>
      <div css={cardStyle} onClick={onDismiss}>
        <img css={iconStyle} src={require('@/static/images/checkIn/identity-verify-ng.svg').default} alt="" />
        <p css={headingStyle}>{heading}</p>
        {retake?.tips && (
          <div css={tipsStyle}>
            <p>{retake.tips.lead}</p>
            <ul>
              {retake.tips.items.map(tip => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </ModalWrapper>
  )
}

// 全画面のカメラ（zIndex 500）の上に出す
const overlayStyle: React.CSSProperties = { zIndex: 600 }

// react-modal の枠は中央に置く入れ物としてだけ使い、見た目は中の cardStyle が持つ。
// `inset` だけ渡すと ModalWrapper 既定の `top: 0` / `left: 0` が後勝ちするため、4 辺を明示する
const cardContainerStyle = {
  top: '1rem',
  right: '1rem',
  bottom: '1rem',
  left: '1rem',
  margin: 'auto',
  width: 'auto',
  maxWidth: '358px',
  height: 'fit-content',
  padding: 0,
  backgroundColor: 'transparent',
  overflow: 'visible',
}

// カメラ映像（黒）とフォーム（施設テーマ）のどちらの上にも重なるため、テーマ色に追従させず固定値にする
const cardStyle = css({
  width: '100%',
  maxWidth: '358px',
  background: '#fff',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.16)',
  padding: '24px',
  textAlign: 'center',
  cursor: 'pointer',
})

const iconStyle = css({
  display: 'block',
  width: '58px',
  height: '58px',
  margin: '0 auto',
})

const headingStyle = css({
  marginTop: '17px',
  color: '#272727',
  fontSize: '18px',
  fontWeight: 'bold',
  letterSpacing: '0.9px',
  lineHeight: '25px',
  whiteSpace: 'pre-line',
})

// 「・」は翻訳文には含めず CSS で付ける
const tipsStyle = css({
  marginTop: '1rem',
  textAlign: 'left',
  background: '#f2f2f2',
  borderRadius: '10px',
  padding: '16px',
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.6px',
  lineHeight: '16px',
  color: '#e6483b',
  // テーマ CSS が `*` で文字色を直接当てるため、子要素にも色を明示しないと継承されない
  'p, li': {
    color: '#e6483b',
  },
  p: {
    margin: 0,
  },
  ul: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  li: {
    '&::before': {
      content: '"・"',
    },
  },
})
